import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { formatNumber } from '../../utils/numberformatter';
import { SkeletonTable } from '../Common/Skeleton';
import { EmptyState } from '../Common/ListStates';

const getLocationId = port =>
  port?.locationId || port?.incotermLocationId || port?.id;

const PortRestrictionsTable = ({ ports = [], procurementId }) => {
  const [jetties, setJetties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!procurementId) {
      setJetties([]);
      return undefined;
    }

    let cancelled = false;
    const token = getAuthToken();

    const loadJettyRestrictions = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const response = await fetch(
          `${api.API_URL}/procurement-port-restriction/by-procurement/${procurementId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'same-origin',
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const items = result?.data ?? result ?? [];

        if (!cancelled) {
          setJetties(Array.isArray(items) ? items : []);
        }
      } catch (_error) {
        if (!cancelled) {
          setJetties([]);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadJettyRestrictions();

    return () => {
      cancelled = true;
    };
  }, [procurementId]);

  const groupedJettyRestrictions = useMemo(() => {
    const groups = new Map();

    ports.forEach(port => {
      const locationId = getLocationId(port);
      if (locationId && !groups.has(locationId)) {
        groups.set(locationId, {
          key: locationId,
          name: port.name || '-',
          items: [],
        });
      }
    });

    jetties.forEach(item => {
      groups.get(item.locationId)?.items.push(item);
    });

    return Array.from(groups.values());
  }, [ports, jetties]);

  if (!ports.length) {
    return (
      <EmptyState
        compact
        icon='ri-anchor-line'
        title='No ports selected'
        emptyHint='Port restrictions are shown for the ports chosen in the delivery terms. No port has been chosen yet.'
      />
    );
  }

  return (
    <div className='table-responsive'>
      <table
        className='table table-hover table-centered align-middle table-nowrap mb-0 bg-white'
        aria-label='Port restrictions'
      >
        <thead className='table-light'>
          <tr>
            <th
              scope='col'
              rowSpan={2}
              className='align-middle'
              style={{ width: '22%' }}
            >
              TERMINAL
            </th>
            <th colSpan={2} className='text-center'>
              DWT (TONS)
            </th>
            <th colSpan={2} className='text-center'>
              LOA (M)
            </th>
            <th colSpan={2} className='text-center'>
              DRAFT (M)
            </th>
          </tr>
          <tr>
            <th className='text-center'>MIN</th>
            <th className='text-center'>MAX</th>
            <th className='text-center'>MIN</th>
            <th className='text-center'>MAX</th>
            <th className='text-center'>MIN</th>
            <th className='text-center'>MAX</th>
          </tr>
        </thead>
        <tbody>
          {loading && <SkeletonTable inline rows={3} columns={7} />}
          {!loading && loadFailed && (
            <tr>
              <td colSpan={7} className='text-center text-danger py-3'>
                Failed to load port restrictions. Please try again later.
              </td>
            </tr>
          )}
          {!loading && !loadFailed && groupedJettyRestrictions.length === 0 && (
            <tr>
              <td colSpan={7}>
                <EmptyState
                  compact
                  icon='ri-anchor-line'
                  title='No port restrictions'
                  emptyHint='No jetty restrictions are registered for the selected ports.'
                />
              </td>
            </tr>
          )}
          {!loading &&
            !loadFailed &&
            groupedJettyRestrictions.map(group => (
              <React.Fragment key={group.key}>
                <tr>
                  <td colSpan={7} className='fw-semibold'>
                    {group.name}
                  </td>
                </tr>
                {group.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className='text-center text-muted ps-4'>
                      No Port data available
                    </td>
                  </tr>
                )}
                {group.items.map(item => (
                  <tr key={item.id}>
                    <td className='ps-4'>{item.name || '-'}</td>
                    <td className='text-center'>{formatNumber(item.dwtMin)}</td>
                    <td className='text-center'>{formatNumber(item.dwtMax)}</td>
                    <td className='text-center'>{formatNumber(item.loaMin)}</td>
                    <td className='text-center'>{formatNumber(item.loaMax)}</td>
                    <td className='text-center'>
                      {formatNumber(item.draftMin)}
                    </td>
                    <td className='text-center'>
                      {formatNumber(item.draftMax)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default PortRestrictionsTable;
